import { useState, useEffect } from 'react';
import { AlertCircle, Delete } from 'lucide-react';

interface Props {
  /** Tamaño del PIN (default 4). */
  longitud?: number;
  /** Etiqueta superior opcional. */
  label?: string;
  /** Callback cuando se completa. */
  onComplete?: (pin: string) => void;
  /** Callback en cada cambio. */
  onChange?: (pin: string) => void;
  /** Mensaje de error opcional. */
  error?: string;
  /** Reset programático del PIN. Cambia el valor (timestamp/random) para forzar limpieza. */
  resetSignal?: unknown;
}

export function PinPad({ longitud = 4, label, onComplete, onChange, error, resetSignal }: Props) {
  const [pin, setPin] = useState('');

  useEffect(() => {
    setPin('');
  }, [resetSignal]);

  useEffect(() => {
    onChange?.(pin);
    if (pin.length === longitud) {
      onComplete?.(pin);
    }
  }, [pin, longitud, onChange, onComplete]);

  const handleDigit = (d: string) => {
    if (pin.length < longitud) setPin((p) => p + d);
  };

  const handleDelete = () => setPin((p) => p.slice(0, -1));

  return (
    <div className="flex flex-col items-center gap-4">
      {label && <div className="text-sm font-medium text-slate-700">{label}</div>}
      <div className="flex gap-3">
        {Array.from({ length: longitud }).map((_, i) => (
          <div
            key={i}
            className={`flex h-14 w-12 items-center justify-center rounded-lg border-2 text-2xl font-bold ${
              error ? 'border-red-400 bg-red-50' : 'border-slate-300 bg-white'
            }`}
          >
            {pin[i] ? '●' : ''}
          </div>
        ))}
      </div>
      {/* Sprint 172 (ux-master #8) — El error no depende sólo del color: lleva ícono. */}
      {error && (
        <div className="flex items-center gap-1.5 text-sm font-medium text-red-700" role="alert">
          <AlertCircle className="h-4 w-4 shrink-0" aria-hidden />
          {error}
        </div>
      )}
      <div className="grid grid-cols-3 gap-2">
        {['1','2','3','4','5','6','7','8','9'].map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => handleDigit(d)}
            className="h-16 w-16 rounded-lg bg-white border border-slate-300 text-xl font-bold hover:bg-slate-100 active:bg-slate-200"
          >
            {d}
          </button>
        ))}
        <div />
        <button
          type="button"
          onClick={() => handleDigit('0')}
          className="h-16 w-16 rounded-lg bg-white border border-slate-300 text-xl font-bold hover:bg-slate-100 active:bg-slate-200"
        >
          0
        </button>
        <button
          type="button"
          onClick={handleDelete}
          className="h-16 w-16 rounded-lg bg-white border border-slate-300 hover:bg-slate-100 active:bg-slate-200 flex items-center justify-center"
          aria-label="Borrar"
        >
          <Delete className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}
